import React, { useMemo } from 'react'
import i18n from '@src/i18n'
import { generatePath } from 'react-router-dom'

import routes from '@src/Routes/routes'
import { CALL_LINK, GUIDES_LINK, YOUTUBE_PLAYLISTS } from '@src/constants'
import {
  IconCircleCheck,
  IconListDetails,
  IconBolt,
  IconBuildingWarehouse,
  IconUsers,
  IconHome,
  IconUsersGroup,
  IconReceipt,
  IconTruckDelivery,
  IconFileInvoice,
  IconCoins,
  IconMail,
  IconFileDollar,
  IconChartInfographic,
  IconNotes,
  IconFileDescription,
  IconSparkles,
} from '@tabler/icons-react'

import { ContactParty } from '@models/ApproveitAccounting'
import { UIFeatures } from '@models/Account'
import { Role } from '@type/common'
import { Feature } from '@type/features'

export interface NavItem {
  to?: string
  label: string
  icon: React.FC<Record<string, unknown>>
  show?: Role[]
  external?: boolean
  onClick?: () => void
  roleVisibilityPermission?: boolean
  feature?: Feature
  isCollapsed?: boolean
  disabled?: boolean
  uiFeatureKey?: keyof UIFeatures
  // Non-plan lock (e.g. beta gating): keeps the item visible and clickable,
  // renders a ContentLock badge, and lets the destination page do the pitch.
  locked?: boolean
  lockTooltip?: string
}

// LEFT ITEMS

export const NAV_ITEMS: NavItem[] = [
  {
    to: routes.private.dashboard,
    icon: IconHome,
    label: i18n.t('common:navigation.dashboard'),
    // No uiFeatureKey - always visible
  },
  {
    to: routes.private.reports,
    icon: IconChartInfographic,
    label: i18n.t('common:navigation.reports'),
    uiFeatureKey: 'reports',
  },
  {
    to: routes.private.assistant,
    icon: IconSparkles,
    label: i18n.t('common:navigation.aiAssistant'),
  },
  {
    to: routes.private.pipelines,
    icon: IconBolt,
    label: i18n.t('common:navigation.workflows'),
    uiFeatureKey: 'workflows',
  },
  {
    to: routes.private.approvalRequests,
    icon: IconCircleCheck,
    label: i18n.t('common:navigation.approvalRequests'),
    // No uiFeatureKey - always visible
  },
  {
    to: routes.private.tasks,
    icon: IconListDetails,
    label: i18n.t('common:navigation.tasks'),
    uiFeatureKey: 'tasks',
  },
  {
    to: routes.private.mailbox,
    icon: IconMail,
    label: i18n.t('common:navigation.mailbox'),
    show: [Role.admin],
    feature: Feature.mailbox,
    uiFeatureKey: 'mailbox',
  },
  {
    to: routes.private.organizationalStructure,
    icon: IconUsersGroup,
    label: i18n.t('common:navigation.team'),
    roleVisibilityPermission: true,
    uiFeatureKey: 'team',
  },
  {
    to: routes.private.costCenter,
    icon: IconCoins,
    label: i18n.t('common:navigation.costCenters'),
    feature: Feature.costCenters,
    uiFeatureKey: 'cost_centers',
  },
]

export const AR_ITEMS: NavItem[] = [
  {
    to: routes.private.invoices,
    icon: IconFileInvoice,
    label: i18n.t('common:navigation.invoices'),
    uiFeatureKey: 'invoices',
  },
  {
    to: routes.private.quotes,
    icon: IconFileDollar,
    label: i18n.t('common:navigation.quotes'),
    uiFeatureKey: 'quotes',
  },
  {
    to: generatePath(routes.private.contacts, { contactParty: ContactParty.customer }),
    icon: IconUsers,
    label: i18n.t('common:navigation.customers'),
    show: [Role.admin],
    uiFeatureKey: 'customers',
  },
]
export const AP_ITEMS: NavItem[] = [
  {
    to: routes.private.bills,
    icon: IconReceipt,
    label: i18n.t('common:navigation.bills'),
    uiFeatureKey: 'bills',
  },
  {
    to: routes.private.purchaseOrders,
    icon: IconTruckDelivery,
    label: i18n.t('common:navigation.purchaseOrders'),
    uiFeatureKey: 'purchase_orders',
  },
  {
    to: routes.private.rfqs,
    icon: IconFileDescription,
    label: i18n.t('common:navigation.rfq'),
    uiFeatureKey: 'rfqs',
  },
  {
    to: generatePath(routes.private.contacts, { contactParty: ContactParty.vendor }),
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.vendors'),
    show: [Role.admin],
    uiFeatureKey: 'vendors',
  },
]
export const CONTRACT_ITEMS: NavItem[] = [
  {
    to: routes.private.contracts,
    icon: IconNotes,
    label: i18n.t('common:navigation.contracts'),
    show: [Role.admin],
    feature: Feature.contractManagement,
    uiFeatureKey: 'contracts',
  },
]

export const ORGANIZATION_MENU: NavItem[] = [
  {
    to: routes.private.products,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.products'),
    show: [Role.admin],
    uiFeatureKey: 'products',
  },
  {
    to: routes.private.accounts,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.accounts'),
    show: [Role.admin],
    uiFeatureKey: 'accounts',
  },
  {
    to: routes.private.accountCategories,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.categories'),
    show: [Role.admin],
    uiFeatureKey: 'categories',
  },
  {
    to: routes.private.taxes,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.taxes'),
    show: [Role.admin],
    uiFeatureKey: 'taxes',
  },
  {
    to: routes.private.companyDetails,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.companyDetails'),
    show: [Role.admin],
  },
]
// HEAD ITEMS

export const QUICK_ACTIONS_ITEMS: NavItem[] = [
  {
    to: routes.private.approvalRequest,
    label: i18n.t('common:navigation.approvalRequest'),
    icon: IconCircleCheck,
    // No uiFeatureKey - always visible
  },
  {
    to: routes.private.taskBoards,
    label: i18n.t('common:navigation.task'),
    icon: IconListDetails,
    uiFeatureKey: 'tasks',
  },
  {
    to: generatePath(routes.private.pipelineById, { id: 'new' }),
    label: i18n.t('common:navigation.workflow'),
    icon: IconBolt,
    show: [Role.admin],
    uiFeatureKey: 'workflows',
  },
  {
    to: generatePath(routes.private.contact, { id: 'new', contactParty: ContactParty.vendor }),
    label: i18n.t('common:navigation.vendor'),
    icon: IconBuildingWarehouse,
    show: [Role.admin],
    uiFeatureKey: 'vendors',
  },
  {
    to: generatePath(routes.private.contact, { id: 'new', contactParty: ContactParty.customer }),
    label: i18n.t('common:navigation.customer'),
    icon: IconUsers,
    show: [Role.admin],
    uiFeatureKey: 'customers',
  },
]

export const useGetResourceItems = (setShowSuggestFeatureModal: (show: boolean) => void) => {
  return useMemo(
    () => [
      { to: GUIDES_LINK, label: i18n.t('common:navigation.allGuides'), external: true },
      { to: YOUTUBE_PLAYLISTS, label: i18n.t('common:navigation.videoGuides'), external: true },
      { to: CALL_LINK, label: i18n.t('common:navigation.bookCall'), external: true },
      { label: i18n.t('common:navigation.suggestFeature'), onClick: () => setShowSuggestFeatureModal(true) },
    ],
    [setShowSuggestFeatureModal],
  )
}
